/**
 * When a resource is open, and what that allows a guest to book.
 *
 * A resource keeps as many windows as its calendar needs, each naming its own weekdays and hours, so
 * 10–6 on Monday, Wednesday and Friday, 12–6 on Tuesday and Thursday, and 8–8 at weekends is three
 * windows rather than a shape the model has to anticipate. Windows may overlap; where several cover a
 * date, the one created most recently decides it, so every date answers with at most one window.
 *
 * Declared as a leaf module so the API, the dashboard and the booking flow all decide it the same way.
 */

import { RecurrencePattern } from './recurrence';
import { AvailabilityTimeFrame, MINUTES_PER_DAY } from './schedule-time-frame';

/**
 * The shape this resolution needs; the full row (ids, timestamps) carries more.
 *
 * Two times of day, and both belong to the same date: `opensAt` is the earliest a booking may start
 * and `closesAt` the latest it may end. Separate pick-up and return bounds were more than any operator
 * has needed so far; if a resource ever wants them, they belong here rather than on the rule.
 */
export interface OperatingHoursWindow {
  /** Null on a row for a resource booked in whole days: the dates and weekdays are the whole rule. */
  closesAt: string | null;
  /** When the row was created. Where windows overlap on a date, the earliest decides it. */
  createdAt?: string | null;
  /** The one resource these hours apply to; null covers every resource in the group. */
  experienceId?: string | null;
  /** Breaks a tie between rows created at the same instant; row ids are time-ordered (UUIDv7). */
  id?: string;
  opensAt: string | null;
  /** Which weekdays it repeats on, or null for a window covering only its start date. */
  recurrencePattern?: RecurrencePattern | null;
  startDate: string;
  until?: string | null;
}

/**
 * The weekdays a window covers.
 *
 * A window with no pattern is the one date it starts on — the same reading a schedule row with no
 * recurrence pattern gets — so its weekday is that date's own.
 */
export const selectWindowWeekdays = (window: OperatingHoursWindow): readonly number[] =>
  window.recurrencePattern?.weekdays ?? [deriveIsoWeekday(window.startDate)];

/** ISO weekday of a "YYYY-MM-DD" date, Monday = 1, matching the stored weekday numbers. */
export const deriveIsoWeekday = (date: string): number => {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  // Noon UTC keeps the day from drifting across a timezone boundary.
  const weekday = new Date(Date.UTC(year, month - 1, day, 12)).getUTCDay();
  return weekday === 0 ? 7 : weekday;
};

const coversDate = (window: OperatingHoursWindow, date: string): boolean =>
  window.startDate <= date && (!window.until || window.until >= date);

/** Newest first: by creation time, then by id (time-ordered) for rows created in the same instant. */
const byNewestCreation = (left: OperatingHoursWindow, right: OperatingHoursWindow): number =>
  (right.createdAt ?? '').localeCompare(left.createdAt ?? '') || (right.id ?? '').localeCompare(left.id ?? '');

/**
 * The window open on `date`, as a list of at most one. Empty means closed that day.
 *
 * Windows may overlap, and where several cover a date the one created most recently decides it — a newer
 * window over the same dates takes over from the older ones. An operator who wants Saturday to read
 * differently gives Saturday its own window and leaves it out of the older ones.
 */
export const selectOperatingHoursForDate = <T extends OperatingHoursWindow>(
  windows: readonly T[],
  date: string,
  experienceId?: string,
): readonly T[] => {
  const weekday = deriveIsoWeekday(date);

  return windows
    .filter(
      (window) =>
        coversDate(window, date) &&
        selectWindowWeekdays(window).includes(weekday) &&
        // A window naming a resource applies to that one; one naming none applies to them all.
        (!window.experienceId || !experienceId || window.experienceId === experienceId),
    )
    .sort(byNewestCreation)
    .slice(0, 1);
};

/** The bounds a booking is held to: a window with no hours constrains nothing but the date. */
export type BookingWindow = Pick<OperatingHoursWindow, 'closesAt' | 'opensAt'>;

export const DAILY_RETURN_AFTER_PICKUP_MESSAGE = 'A daily booking must be returned after its pickup time';

/**
 * Whether the hours break the daily frame's rule that the return comes after the pickup.
 *
 * A daily booking is picked up and returned within the same day, so a return at or before the pickup
 * would end before it began. A nightly one is returned the next morning, which reads as an earlier
 * time of day, so only the daily frame is held to this. Hours with either time missing say nothing
 * about a return.
 */
export const isDailyReturnNotAfterPickup = (
  timeFrame: AvailabilityTimeFrame | null | undefined,
  window: BookingWindow,
): boolean =>
  timeFrame === AvailabilityTimeFrame.DAILY && !!window.opensAt && !!window.closesAt && window.closesAt <= window.opensAt;

const acceptsStart = (window: BookingWindow, startTime: string): boolean =>
  (!window.opensAt || startTime >= window.opensAt) && (!window.closesAt || startTime <= window.closesAt);

const acceptsEnd = (window: BookingWindow, endTime: string): boolean =>
  (!window.closesAt || endTime <= window.closesAt) && (!window.opensAt || endTime >= window.opensAt);

/**
 * Why a booking does not fit the day's windows, or null when one of them takes it.
 *
 * A booking has to fit inside a single window: starting in the morning session and ending in the
 * afternoon one would run straight through a closure. `'start'` when no window would take the pick-up
 * at all, `'end'` when one would but none accepts the return.
 */
export const findBookingWindowViolation = (
  windows: readonly BookingWindow[],
  startTime: string,
  endTime: string,
): 'start' | 'end' | null => {
  if (!windows.length) {
    return null;
  }

  if (windows.some((window) => acceptsStart(window, startTime) && acceptsEnd(window, endTime))) {
    return null;
  }

  return windows.some((window) => acceptsStart(window, startTime)) ? 'end' : 'start';
};

const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * How a weekday set reads: "Every day", "Weekdays", "Weekends", or the days themselves.
 *
 * Any combination is storable, so any combination has to be describable — Monday, Wednesday and Friday
 * is not a special case, it is just what that resource does.
 */
export const describeWeekdays = (weekdays: readonly number[]): string => {
  const unique = [...new Set(weekdays)].sort((left, right) => left - right);
  const key = unique.join(',');

  if (key === '1,2,3,4,5,6,7') {
    return 'Every day';
  }
  if (key === '1,2,3,4,5') {
    return 'Weekdays';
  }
  if (key === '6,7') {
    return 'Weekends';
  }

  return unique.map((weekday) => WEEKDAY_NAMES[weekday - 1] ?? `${weekday}`).join(', ');
};

/** Minutes since midnight for an "HH:mm" (or "HH:mm:ss") time. */
/** A time of day, "HH:mm", as minutes since midnight. */
export const toMinutesOfDay = (time: string): number => {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/** "HH:mm" for minutes since midnight. */
const toTimeOfDay = (minutes: number): string =>
  `${`${Math.floor(minutes / 60)}`.padStart(2, '0')}:${`${minutes % 60}`.padStart(2, '0')}`;

/** The duration rules a booking is held to, as the availability rule states them. */
export interface BookingDurationRules {
  /** How often a booking may begin: 9:00, 9:15, 9:30 for a 15 minute increment. */
  bookingIncrementMinutes: number;
  /** The step between offered lengths. Falls back to the booking increment when a rule has none. */
  durationIncrementMinutes?: number | null;
  maxDurationMinutes?: number | null;
  minDurationMinutes: number;
}

/** The bounds a booking must sit inside, as minutes since midnight. */
const toWindowBounds = (window: OperatingHoursWindow): { closes: number; opens: number } | null =>
  // A window with no hours belongs to a resource booked in whole days: the date is the whole booking,
  // and there are no times of day to offer.
  window.opensAt && window.closesAt
    ? { closes: toMinutesOfDay(window.closesAt), opens: toMinutesOfDay(window.opensAt) }
    : null;

/**
 * The times a booking may start on a date, from the windows open that day and the resource's rules.
 *
 * A start is offered only when the shortest booking still fits before closing — a resource open until
 * 6pm with a one hour minimum stops offering starts at 5pm. The times come from the window that decides
 * the date (see `selectOperatingHoursForDate`).
 *
 * A resource booked in whole days has no hours on its windows and so no start times.
 */
export const deriveBookableStartTimes = ({
  bookingIncrementMinutes,
  date,
  experienceId,
  minDurationMinutes,
  windows,
}: {
  bookingIncrementMinutes: number;
  date: string;
  experienceId?: string;
  minDurationMinutes: number;
  windows: readonly OperatingHoursWindow[];
}): readonly string[] => {
  const increment = Math.max(bookingIncrementMinutes, 1);

  const starts = selectOperatingHoursForDate(windows, date, experienceId).flatMap((window) => {
    const bounds = toWindowBounds(window);
    if (!bounds) {
      return [];
    }

    const lastStart = bounds.closes - minDurationMinutes;
    const slots = Math.floor((lastStart - bounds.opens) / increment) + 1;

    return slots > 0 ? Array.from({ length: slots }, (_, index) => toTimeOfDay(bounds.opens + index * increment)) : [];
  });

  return [...new Set(starts)].sort();
};

/**
 * The lengths a booking may run from a chosen start, shortest first.
 *
 * Bounded by the window the start falls in — a booking has to end before that window closes — and by
 * the rule's own maximum. Offered in steps of the duration increment, so an operator who sells in half
 * hours is not offered a 40 minute booking. The window is the one that decides the date (see
 * `selectOperatingHoursForDate`).
 */
export const deriveBookableDurations = ({
  date,
  experienceId,
  rules,
  startTime,
  windows,
}: {
  date: string;
  experienceId?: string;
  rules: BookingDurationRules;
  startTime: string;
  windows: readonly OperatingHoursWindow[];
}): readonly number[] => {
  const start = toMinutesOfDay(startTime);
  const open = selectOperatingHoursForDate(windows, date, experienceId)
    .map(toWindowBounds)
    .filter((bounds): bounds is { closes: number; opens: number } => !!bounds)
    .filter((bounds) => bounds.opens <= start && start < bounds.closes);

  // No hours to be inside means nothing constrains the length but the rule itself.
  const untilClosing = open.length ? Math.max(...open.map((bounds) => bounds.closes)) - start : null;
  const longest = Math.min(...[rules.maxDurationMinutes, untilClosing].filter((value): value is number => value != null));

  if (!Number.isFinite(longest) || longest < rules.minDurationMinutes) {
    return [];
  }

  const step = Math.max(rules.durationIncrementMinutes ?? rules.bookingIncrementMinutes, 1);
  const rungs = Math.floor((longest - rules.minDurationMinutes) / step) + 1;

  return Array.from({ length: rungs }, (_, index) => rules.minDurationMinutes + index * step);
};

/** The time of day a booking of `durationMinutes` from `startTime` ends, wrapping past midnight. */
export const deriveEndTimeOfDay = (startTime: string, durationMinutes: number): string =>
  toTimeOfDay((toMinutesOfDay(startTime) + durationMinutes) % MINUTES_PER_DAY);

/** Whether a resource is open at all on a date, and so whether it can be booked on it. */
export const isDateOpen = (windows: readonly OperatingHoursWindow[], date: string, experienceId?: string): boolean =>
  !windows.length || selectOperatingHoursForDate(windows, date, experienceId).length > 0;

/**
 * How many billable units a stay from `from` to `to` runs to, both dates inclusive of the guest's use.
 *
 * A daily resource is counted in days a guest holds it: Friday to Sunday is three days of kit. A nightly
 * one is counted in nights slept: the same dates are two. Either way a single date is one unit, since a
 * booking cannot be for nothing.
 */
export const countBookingDays = ({
  from,
  timeFrame,
  to,
}: {
  from: string;
  timeFrame: AvailabilityTimeFrame;
  to: string;
}): number => {
  const startMs = Date.parse(`${from}T12:00:00Z`);
  const endMs = Date.parse(`${to}T12:00:00Z`);
  const nights = Math.max(Math.round((endMs - startMs) / (24 * 60 * 60 * 1000)), 0);

  return timeFrame === AvailabilityTimeFrame.NIGHTLY ? Math.max(nights, 1) : nights + 1;
};

const addDays = (date: string, days: number): string =>
  new Date(Date.parse(`${date}T00:00:00Z`) + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

export interface DayBookingSpan {
  durationMinutes: number;
  /** Local "YYYY-MM-DDTHH:mm:ss". */
  endDateTime: string;
  /** Local "YYYY-MM-DDTHH:mm:ss". */
  startDateTime: string;
  /** Days held for a daily resource, nights for a nightly one. */
  units: number;
}

/**
 * The span of clock time a day-based booking runs for, from the dates picked and the pickup and return
 * times on the availability rows that apply.
 *
 * A daily resource is out from pickup on the first day to return on the last: a golf cart from 8am
 * Monday to 6pm Tuesday is two days. A nightly resource is out from pickup on the first date to return
 * on the date after its last night: an amenity from 3pm Monday to 11am Tuesday is one night. The times
 * are whatever the operator set — a day can be returned the next morning too — and a row with no times
 * runs midnight to midnight, as it always has.
 *
 * The span is what the booking flow sends and what the calculator prices — one whole unit per day or
 * night, since it never splits a day — so the count here and the count charged are one figure.
 */
export const deriveDayBookingSpan = ({
  endsAt,
  from,
  startsAt,
  timeFrame,
  to,
}: {
  /** The return time on the last day, "HH:mm"; null for midnight. */
  endsAt?: string | null;
  from: string;
  /** The pickup time on the first day, "HH:mm"; null for midnight. */
  startsAt?: string | null;
  timeFrame: AvailabilityTimeFrame;
  to: string;
}): DayBookingSpan => {
  const units = countBookingDays({ from, timeFrame, to });

  // A night is returned on the day after its last one. A day is returned on its last day — unless the
  // return time is at or before the pickup time, which says the return is the following morning (a
  // cart out 9am to 9am, or the legacy midnight-to-midnight day).
  const returnsNextDay = timeFrame === AvailabilityTimeFrame.NIGHTLY || (endsAt ?? '00:00') <= (startsAt ?? '00:00');
  const endDate = returnsNextDay ? addDays(from, units) : addDays(from, units - 1);

  const startDateTime = `${from}T${startsAt ?? '00:00'}:00`;
  const endDateTime = `${endDate}T${endsAt ?? '00:00'}:00`;

  return {
    durationMinutes: Math.round((Date.parse(`${endDateTime}Z`) - Date.parse(`${startDateTime}Z`)) / 60_000),
    endDateTime,
    startDateTime,
    units,
  };
};

/**
 * The same span, with the pickup and return times read off the availability rows: pickup from the row
 * open on the first day, return from the row open on the day the booking ends. Windows are per date, so
 * a winter season can hand a cart back earlier than a summer one.
 */
export const deriveDayBookingSpanFromWindows = ({
  experienceId,
  from,
  timeFrame,
  to,
  windows,
}: {
  experienceId?: string;
  from: string;
  timeFrame: AvailabilityTimeFrame;
  to: string;
  windows: readonly OperatingHoursWindow[];
}): DayBookingSpan => {
  const units = countBookingDays({ from, timeFrame, to });
  const [firstDay] = selectOperatingHoursForDate(windows, from, experienceId);
  const lastDate = timeFrame === AvailabilityTimeFrame.NIGHTLY ? addDays(from, units) : addDays(from, units - 1);
  const [lastDay] = selectOperatingHoursForDate(windows, lastDate, experienceId);

  return deriveDayBookingSpan({
    endsAt: (lastDay ?? firstDay)?.closesAt ?? null,
    from,
    startsAt: firstDay?.opensAt ?? null,
    timeFrame,
    to,
  });
};

/**
 * How many days or nights a span of clock time counts as — the inverse of `deriveDayBookingSpan`, for
 * a booking that arrives as a start and an end.
 *
 * Every calendar date the span touches is a day, except a final date reached only to hand the resource
 * back: a return at or before the pickup hour (9am to 9am, midnight to midnight) is the morning after
 * the last day, not a day of its own. A nightly booking never counts its return date — that is the
 * morning after the last night — and is at least one night.
 */
export const countDayUnitsBetween = ({
  endDateTime,
  startDateTime,
  timeFrame,
}: {
  /** Local "YYYY-MM-DDTHH:mm:ss". */
  endDateTime: string;
  /** Local "YYYY-MM-DDTHH:mm:ss". */
  startDateTime: string;
  timeFrame: AvailabilityTimeFrame;
}): number => {
  const startDate = startDateTime.slice(0, 10);
  const endDate = endDateTime.slice(0, 10);
  const datesTouched =
    Math.round((Date.parse(`${endDate}T12:00:00Z`) - Date.parse(`${startDate}T12:00:00Z`)) / (24 * 60 * 60 * 1000)) + 1;

  if (timeFrame === AvailabilityTimeFrame.NIGHTLY) {
    return Math.max(datesTouched - 1, 1);
  }

  const returnsNextDay = endDateTime.slice(11, 16) <= startDateTime.slice(11, 16) && datesTouched > 1;
  return Math.max(returnsNextDay ? datesTouched - 1 : datesTouched, 1);
};
